"use client";

import Link from "next/link";
import { createContext, useContext, type ReactNode } from "react";
import { useClaritySession } from "@/lib/auth/auth-store";
import {
  canAccessTenantId,
  createTenantHref,
  getFirstAccessibleTenantId,
  isValidTenantId,
} from "@/lib/auth/tenant-access";

type TenantRouteContextValue = {
  tenantId: string;
};

export const TenantRouteContext = createContext<TenantRouteContextValue | null>(null);

function TenantAccessError({
  fallbackTenantId,
}: {
  fallbackTenantId: string | null;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl rounded-4xl border border-border bg-surface p-8 shadow-card sm:p-10">
        <p className="text-label-md uppercase tracking-[0.16em] text-content-tertiary">
          Practice Access
        </p>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-[-0.02em] text-foreground sm:text-5xl">
          This account cannot open that practice.
        </h1>
        <p className="mt-4 max-w-xl text-body-lg text-content-secondary">
          The route selected a practice context, but your current session claims
          do not include a matching practiceId.
        </p>
        {fallbackTenantId ? (
          <div className="mt-8">
            <Link
              href={createTenantHref(fallbackTenantId)}
              className="inline-flex items-center rounded-2xl border border-border px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-surface-muted"
            >
              Open {fallbackTenantId}
            </Link>
          </div>
        ) : (
          <p className="mt-6 max-w-xl text-body-md text-content-secondary">
            No practice access is currently available for this account.
          </p>
        )}
      </div>
    </div>
  );
}

export function TenantRouteProvider({
  children,
  tenantId,
}: {
  children: ReactNode;
  tenantId: string;
}) {
  const session = useClaritySession();
  const normalizedTenantId = tenantId.trim();
  const fallbackTenantId = getFirstAccessibleTenantId(session.tenantIds);
  const canOpenTenant =
    isValidTenantId(normalizedTenantId) &&
    canAccessTenantId(normalizedTenantId, session.tenantIds);

  if (!canOpenTenant) {
    return <TenantAccessError fallbackTenantId={fallbackTenantId} />;
  }

  return (
    <TenantRouteContext.Provider value={{ tenantId: normalizedTenantId }}>
      {children}
    </TenantRouteContext.Provider>
  );
}

export function useTenantRouteContext() {
  const context = useContext(TenantRouteContext);

  if (!context) {
    throw new Error(
      "useTenantRouteContext must be used inside a tenant-scoped route.",
    );
  }

  return context;
}
